// @tennis-matches/services/appearance/definitions
// Migrated from src/services/appearance/definitions.js
// Whitelist für veränderbare UI-Design-Tokens (Governance + Validierung).

/**
 * @typedef {"code"|"superadmin"|"admin"|"user"} WriteLevel
 * @typedef {"color"|"px"|"string"} TokenType
 *
 * @typedef {Object} AppearanceTokenDefinition
 * @property {string} key - stabiler Settings-Key (API/DB)
 * @property {string} cssVar - CSS custom property (z.B. "--primary")
 * @property {TokenType} type
 * @property {WriteLevel} minWriteLevel
 * @property {string} label
 * @property {string} description
 * @property {string=} defaultValue - optionaler Default (ansonsten aus CSS)
 */

const HEX_COLOR_RE = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR_RE = /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(\s*,\s*(0|1|0?\.\d+))?\s*\)$/i;

function __isColor(v) {
  const s = String(v || "").trim();
  return HEX_COLOR_RE.test(s) || RGB_COLOR_RE.test(s);
}

function __isPx(v) {
  const s = String(v || "").trim();
  if (!/^\d+(\.\d+)?px$/i.test(s)) return false;
  const n = Number(s.toLowerCase().replace("px", ""));
  return Number.isFinite(n);
}

/** @type {AppearanceTokenDefinition[]} */
export const APPEARANCE_TOKENS_V1 = [
  // Surfaces
  {
    key: "ui.theme.bg",
    cssVar: "--bg",
    type: "color",
    minWriteLevel: "superadmin",
    label: "Hintergrund",
    description: "App-Hintergrundfarbe (Seitenhintergrund).",
  },
  {
    key: "ui.theme.surface",
    cssVar: "--surface",
    type: "color",
    minWriteLevel: "superadmin",
    label: "Surface",
    description: "Karten/Flächen-Hintergrund.",
  },

  // Text
  {
    key: "ui.theme.text",
    cssVar: "--text",
    type: "color",
    minWriteLevel: "superadmin",
    label: "Text",
    description: "Standard-Textfarbe.",
  },
  {
    key: "ui.theme.muted",
    cssVar: "--muted",
    type: "color",
    minWriteLevel: "superadmin",
    label: "Muted",
    description: "Sekundär-/Hinweistextfarbe.",
  },

  // Brand
  {
    key: "ui.theme.primary",
    cssVar: "--primary",
    type: "color",
    minWriteLevel: "superadmin",
    label: "Primary",
    description: "Primär-/Akzentfarbe (Buttons, Highlights).",
  },
  {
    key: "ui.theme.primaryHover",
    cssVar: "--primaryHover",
    type: "color",
    minWriteLevel: "superadmin",
    label: "Primary Hover",
    description: "Hover-Farbe für Primary-Elemente.",
  },
  {
    key: "ui.theme.primaryText",
    cssVar: "--primaryText",
    type: "color",
    minWriteLevel: "superadmin",
    label: "Primary Text",
    description: "Textfarbe auf Primary-Flächen.",
  },

  // Radius (vorsichtig: zu starke Änderungen beeinflussen Layout)
  {
    key: "ui.theme.radiusSm",
    cssVar: "--radius-sm",
    type: "px",
    minWriteLevel: "superadmin",
    label: "Radius (small)",
    description: "Kleine Rundung (px).",
  },
  {
    key: "ui.theme.radius",
    cssVar: "--radius",
    type: "px",
    minWriteLevel: "superadmin",
    label: "Radius",
    description: "Standard-Rundung (px).",
  },
  {
    key: "ui.theme.radiusLg",
    cssVar: "--radius-lg",
    type: "px",
    minWriteLevel: "superadmin",
    label: "Radius (large)",
    description: "Große Rundung (px).",
  },
];

/** Fast lookup by key. */
export const APPEARANCE_TOKENS_BY_KEY = Object.freeze(
  APPEARANCE_TOKENS_V1.reduce((acc, d) => {
    acc[d.key] = d;
    return acc;
  }, /** @type {Record<string, AppearanceTokenDefinition>} */ ({}))
);

/**
 * Validiert einen Wert gegen die Definition.
 * @param {AppearanceTokenDefinition} def
 * @param {any} value
 * @returns {{ok: boolean, value?: string, reason?: string}}
 */
export function validateAppearanceValue(def, value) {
  if (!def || typeof def !== "object") return { ok: false, reason: "missing-def" };
  const v = String(value ?? "").trim();
  if (!v) return { ok: false, reason: "empty" };

  if (def.type === "color") {
    return __isColor(v) ? { ok: true, value: v } : { ok: false, reason: "invalid-color" };
  }
  if (def.type === "px") {
    // Guardrails: limit extremes
    if (!__isPx(v)) return { ok: false, reason: "invalid-px" };
    const n = Number(v.toLowerCase().replace("px", ""));
    if (n < 0 || n > 64) return { ok: false, reason: "out-of-range" };
    return { ok: true, value: `${n}px` };
  }

  // string: last resort (not used in v1)
  return { ok: true, value: v };
}

/**
 * Extrahiert aus einem Overrides-Objekt nur erlaubte Keys + validiert Werte.
 * @param {Record<string, any>} overrides
 * @returns {{ok: true, values: Record<string,string>, warnings: Record<string,string>} | {ok: false, reason: string}}
 */
export function sanitizeAppearanceOverrides(overrides) {
  const src = (overrides && typeof overrides === "object") ? overrides : null;
  if (!src) return { ok: false, reason: "invalid-overrides" };

  /** @type {Record<string,string>} */
  const values = {};
  /** @type {Record<string,string>} */
  const warnings = {};

  for (const [k, raw] of Object.entries(src)) {
    const def = APPEARANCE_TOKENS_BY_KEY[k];
    if (!def) {
      warnings[k] = "unknown-key";
      continue;
    }
    const res = validateAppearanceValue(def, raw);
    if (!res.ok) {
      warnings[k] = res.reason || "invalid";
      continue;
    }
    values[k] = res.value;
  }

  return { ok: true, values, warnings };
}
